import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  fetchAsnLocationDropdownFilters,
  AsnLocationFilterRequest,
  AsnLocationFilterResponse,
  DropdownOption,
  LocationOption,
} from '../api/asnLocationDropdownApi';
import { KPIFilterDropdownItem } from '../../../../../screens/AnalyticsHub/components/KPIFilterDropdown';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseAsnLocationDropdownsOptions {
  debounceMs?: number;
  enabled?: boolean;
  skipInitialFetch?: boolean; // Skip the initial empty fetch (e.g., when auto-population will happen)
}

export interface UseAsnLocationDropdownsReturn {
  regionOptions: KPIFilterDropdownItem[];
  marketOptions: KPIFilterDropdownItem[];
  locationOptions: KPIFilterDropdownItem[];
  receivingLocationOptions: KPIFilterDropdownItem[];
  loading: boolean;
  error: Error | null;
  refreshDropdowns: () => Promise<void>;
  lastRefreshedAt: string | null;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────

const optionToDropdownItem = (option: DropdownOption): KPIFilterDropdownItem => ({
  id: option.value,
  displayText: option.label,
});

const locationOptionToDropdownItem = (option: LocationOption): KPIFilterDropdownItem => ({
  id: option.locId,
  displayText: option.label,
});

const extractDropdownItemIds = (items: KPIFilterDropdownItem[]): string[] => {
  return items.map((item) => String(item.id)).filter((id) => id);
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useAsnLocationDropdowns = (
  selectedRegions: KPIFilterDropdownItem[],
  selectedMarkets: KPIFilterDropdownItem[],
  selectedLocations: KPIFilterDropdownItem[],
  options?: UseAsnLocationDropdownsOptions
): UseAsnLocationDropdownsReturn => {
  const { debounceMs = 300, enabled = true, skipInitialFetch = false } = options || {};

  const [apiResponse, setApiResponse] = useState<AsnLocationFilterResponse>({
    shippingRegions: [],
    shippingMarkets: [],
    shippingLocations: [],
    receivingLocations: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousRequestRef = useRef<AsnLocationFilterRequest | null>(null);

  // Memoize dropdown options to prevent infinite re-render loops.
  // Without memoization, .map() creates new array references on every render,
  // causing parent components' dependencies to "change" even when apiResponse hasn't changed.
  const regionOptions = useMemo(
    () => apiResponse.shippingRegions.map(optionToDropdownItem),
    [apiResponse.shippingRegions]
  );
  const marketOptions = useMemo(
    () => apiResponse.shippingMarkets.map(optionToDropdownItem),
    [apiResponse.shippingMarkets]
  );
  const locationOptions = useMemo(
    () => apiResponse.shippingLocations.map(locationOptionToDropdownItem),
    [apiResponse.shippingLocations]
  );
  const receivingLocationOptions = useMemo(
    () => apiResponse.receivingLocations.map(locationOptionToDropdownItem),
    [apiResponse.receivingLocations]
  );

  const buildRequest = useCallback((): AsnLocationFilterRequest => {
    return {
      shippingRegions: extractDropdownItemIds(selectedRegions),
      shippingMarkets: extractDropdownItemIds(selectedMarkets),
      shippingLocationIds: extractDropdownItemIds(selectedLocations),
      receivingLocationIds: [],
    };
  }, [selectedRegions, selectedMarkets, selectedLocations]);

  const hasRequestChanged = useCallback((newRequest: AsnLocationFilterRequest): boolean => {
    const prev = previousRequestRef.current;
    if (!prev) return true;

    const compare = (arr1: string[] = [], arr2: string[] = []): boolean => {
      if (arr1.length !== arr2.length) return true;
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      return sorted1.some((val, idx) => val !== sorted2[idx]);
    };

    return (
      compare(newRequest.shippingRegions, prev.shippingRegions) ||
      compare(newRequest.shippingMarkets, prev.shippingMarkets) ||
      compare(newRequest.shippingLocationIds, prev.shippingLocationIds) ||
      compare(newRequest.receivingLocationIds, prev.receivingLocationIds)
    );
  }, []);

  const performFetch = useCallback(async (request: AsnLocationFilterRequest) => {
    if (!enabled) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetchAsnLocationDropdownFilters(request);
      setApiResponse(response);
      previousRequestRef.current = request;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('useAsnLocationDropdowns error:', error);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const newRequest = buildRequest();
      if (hasRequestChanged(newRequest)) {
        performFetch(newRequest);
      }
    }, debounceMs);
  }, [buildRequest, hasRequestChanged, performFetch, debounceMs]);

  const refreshDropdowns = useCallback(async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    const request = buildRequest();
    await performFetch(request);
  }, [buildRequest, performFetch]);

  // ─── Effect for region/market changes (debounced for performance) ─────────────
  useEffect(() => {
    debouncedFetch();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [debouncedFetch, selectedRegions, selectedMarkets]);

  // ─── Immediate effect for location changes (non-debounced) ────────────────────
  // When locations are selected, make an immediate API call to fetch receiving locations.
  // This ensures receiving locations populate instantly without 300ms debounce delay.
  // Regions/Markets are passed in the payload if selected, or as empty arrays if not.
  useEffect(() => {
    if (!enabled) return;
    if (selectedLocations.length === 0) return;

    const newRequest = buildRequest();
    if (hasRequestChanged(newRequest)) {
      // Clear any pending debounced calls to avoid conflicts
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Make an immediate call without debounce for receiving locations
      performFetch(newRequest);
    }
  }, [selectedLocations, enabled, buildRequest, hasRequestChanged, performFetch]);

  useEffect(() => {
    if (!enabled) return;
    if (skipInitialFetch) return; // Skip initial fetch if told to skip (e.g., during auto-population)

    if (previousRequestRef.current === null && !loading) {
      performFetch({});
    }
  }, [enabled, skipInitialFetch, loading, performFetch]);

  return {
    regionOptions,
    marketOptions,
    locationOptions,
    receivingLocationOptions,
    loading,
    error,
    refreshDropdowns,
    lastRefreshedAt: apiResponse.lastRefreshedAt ?? null,
  };
};
